import { useParams, Link } from "react-router-dom";
import {
  usePublicFile,
  usePublicFileInsights,
  usePublicFileIncentives,
  type PublicFileInsight,
  type PublicFileIncentive
} from "@swivu/client-api";
import {
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  MagnifyingGlassPlusIcon,
  PhotoIcon,
  SparklesIcon,
  TrophyIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import ImageZoom from "@swivu/shared-ui/src/components/ImageZoom.js";
import QuinnIcon from "../icons/Quinn.svg?react";
import HankIcon from "../icons/Hank.svg?react";
import GraceIcon from "../icons/Grace.svg?react";
import { cn } from "@swivu/shared-ui/src/private/utils.js";
import { Popover, TabsCapsule } from "@swivu/shared-ui";

// ============================================================================
// Utility Functions
// ============================================================================

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageFile = (name?: string | null): boolean => {
  if (!name) return false;
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "avif"].includes(ext);
};

// ============================================================================
// Types
// ============================================================================

type GroupedInsight = {
  insight_type: string;
  messages: Array<{
    id: number;
    content: string;
    created_by: string | null;
  }>;
};

// ============================================================================
// Insight Configuration
// ============================================================================

type Insight_type =
  | "analyze"
  | "review"
  | "execute"
  | "site_safety"
  | "workmanship";
const insightConfig: Record<
  Insight_type,
  {
    name: string;
    role: string;
    Icon: React.ComponentType<{ className?: string }>;
    gradient: string;
    iconBg: string;
    iconColor: string;
    badgeColor: string;
  }
> = {
  analyze: {
    name: "Quinn",
    role: "Troubleshooter",
    Icon: QuinnIcon,
    gradient: "from-blue-500/10 via-blue-500/5 to-transparent",
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    badgeColor:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
  },
  execute: {
    name: "Hank",
    role: "Builder / Mechanic",
    Icon: HankIcon,
    gradient: "from-emerald-500/10 via-emerald-500/5 to-transparent",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    badgeColor:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
  },
  review: {
    name: "Grace",
    role: "Customer Advocate",
    Icon: GraceIcon,
    gradient: "from-violet-500/10 via-violet-500/5 to-transparent",
    iconBg: "bg-violet-100 dark:bg-violet-900/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    badgeColor:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
  },
  site_safety: {
    name: "Safety",
    role: "Site Safety",
    Icon: SparklesIcon,
    gradient: "from-amber-500/10 via-amber-500/5 to-transparent",
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    badgeColor:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
  },
  workmanship: {
    name: "Quality",
    role: "Workmanship",
    Icon: SparklesIcon,
    gradient: "from-teal-500/10 via-teal-500/5 to-transparent",
    iconBg: "bg-teal-100 dark:bg-teal-900/40",
    iconColor: "text-teal-600 dark:text-teal-400",
    badgeColor:
      "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
  }
};

const defaultInsightConfig = {
  name: "Insight",
  role: "Analysis",
  Icon: SparklesIcon,
  gradient: "from-mono-500/10 via-mono-500/5 to-transparent",
  iconBg: "bg-mono-100 dark:bg-mono-800",
  iconColor: "text-mono-600 dark:text-mono-400",
  badgeColor: "bg-mono-100 text-mono-700 dark:bg-mono-800 dark:text-mono-300"
};

// ============================================================================
// Helper Functions
// ============================================================================

const groupInsightsByType = (
  insights: PublicFileInsight[]
): GroupedInsight[] => {
  const grouped = new Map<string, GroupedInsight["messages"]>();

  for (const insight of insights) {
    const messages = grouped.get(insight.insight_type) ?? [];
    messages.push({
      id: insight.id,
      content: insight.content,
      created_by: insight.created_by
    });
    grouped.set(insight.insight_type, messages);
  }

  // Order insights by priority: analyze first, then review, execute, safety, workmanship
  const order = ["analyze", "review", "execute", "site_safety", "workmanship"];
  const entries = Array.from(grouped.entries());
  entries.sort((a, b) => {
    const indexA = order.indexOf(a[0]);
    const indexB = order.indexOf(b[0]);
    return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
  });

  return entries.map(([insight_type, messages]) => ({
    insight_type,
    messages
  }));
};

// ============================================================================
// Sub-Components
// ============================================================================

const InsightCard = ({ group }: { group: GroupedInsight }) => {
  const config =
    insightConfig[group.insight_type as Insight_type] ?? defaultInsightConfig;
  const { Icon } = config;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      {/* Gradient background */}
      <div
        className={cn("absolute inset-0 bg-linear-to-br", config.gradient)}
      />

      <div className="relative p-4 sm:p-5">
        {/* Header with avatar and role badge */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className={cn(
              "shrink-0 size-11 rounded-full flex items-center justify-center shadow-sm",
              config.iconBg
            )}
          >
            <Icon className={cn("size-5", config.iconColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{config.name}</h3>
              <span
                className={cn(
                  "text-xs px-2 py-0.5 rounded-full font-medium",
                  config.badgeColor
                )}
              >
                {config.role}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {config.role}
            </p>
          </div>
        </div>

        {/* Insight messages */}
        <div className="space-y-3">
          {group.messages.map((message) => (
            <div
              key={message.id}
              className="relative pl-4 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:rounded-full before:bg-border"
            >
              <div
                className="prose-xs text-foreground/90 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const IncentiveCard = ({ incentive }: { incentive: PublicFileIncentive }) => {
  const statusStyles = {
    approved:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    pending:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
  };

  const statusClass =
    statusStyles[incentive.status as keyof typeof statusStyles] ??
    "bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-400";

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-4 transition-all hover:shadow-md hover:border-amber-200 dark:hover:border-amber-800/50">
      {/* Subtle gradient on hover */}
      <div className="absolute inset-0 bg-linear-to-r from-amber-500/0 via-amber-500/5 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative flex items-center gap-4">
        <div className="shrink-0 size-12 rounded-xl bg-linear-to-br from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-900/10 flex items-center justify-center shadow-sm">
          <TrophyIcon className="size-6 text-amber-600 dark:text-amber-400" />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground truncate">
            {incentive.name}
          </h4>
          {incentive.task_name && (
            <p className="text-sm text-muted-foreground truncate">
              {incentive.task_name}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {incentive.points}
            </span>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              pts
            </span>
          </div>
          <span
            className={cn(
              "text-xs px-2 py-0.5 rounded-full font-medium capitalize",
              statusClass
            )}
          >
            {incentive.status}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Loading Skeleton
// ============================================================================

const FileDetailSkeleton = () => (
  <div className="min-h-screen bg-background">
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Image skeleton */}
      <div className="aspect-4/3 sm:aspect-video rounded-2xl bg-mono-200 dark:bg-mono-800 animate-pulse" />

      {/* Title skeleton */}
      <div className="space-y-2">
        <div className="h-7 w-3/4 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
      </div>

      {/* Insights skeleton */}
      <div className="space-y-4">
        <div className="h-5 w-24 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
        {[1, 2].map((i) => (
          <div
            key={i}
            className="rounded-2xl border border-border p-5 space-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="size-11 rounded-full bg-mono-200 dark:bg-mono-800 animate-pulse" />
              <div className="space-y-2">
                <div className="h-4 w-24 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                <div className="h-3 w-16 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
              <div className="h-3 w-4/5 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const ViewFile = () => {
  const { slug, file_id } = useParams<{ slug: string; file_id: string }>();

  const { data: file, isLoading: isFileLoading } = usePublicFile({
    variables: { slug: slug!, id: file_id! },
    enabled: !!slug && !!file_id
  });

  const { data: insights, isLoading: isInsightsLoading } =
    usePublicFileInsights({
      variables: { slug: slug!, id: file_id! },
      enabled: !!slug && !!file_id
    });

  const { data: incentives, isLoading: isIncentivesLoading } =
    usePublicFileIncentives({
      variables: { slug: slug!, id: file_id! },
      enabled: !!slug && !!file_id
    });

  if (isFileLoading) {
    return <FileDetailSkeleton />;
  }

  if (!file) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-4 md:px-4">
        <div className="text-center space-y-4">
          <div className="size-16 mx-auto rounded-full bg-mono-100 dark:bg-mono-900 flex items-center justify-center">
            <DocumentTextIcon className="size-8 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              File not found
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              This file may have been removed or the link is incorrect.
            </p>
          </div>
          <Link
            to={`/p/${slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeftIcon className="size-4" />
            Back to schedule
          </Link>
        </div>
      </div>
    );
  }

  const hasImage = file.thumbnail_url || isImageFile(file.file_name);
  const imageUrl = file.url || file.thumbnail_url;
  const fileName = file.description || file.file_name;
  const groupedInsights = insights ? groupInsightsByType(insights) : [];
  const hasInsights = groupedInsights.length > 0;
  const hasIncentives = incentives && incentives.length > 0;

  const metadata = [
    {
      label: "Size",
      value: formatFileSize(file.file_size),
      show: file.file_size > 0
    },
    { label: "Source", value: file.source, show: !!file.source },
    { label: "Uploaded", value: file.age, show: !!file.age },
    { label: "By", value: file.uploaded_by, show: !!file.uploaded_by }
  ].filter((item) => item.show);

  return (
    <div className="min-h-screen bg-background pb-safe">
      <main className="max-w-4xl mx-auto  py-6 space-y-8">
        {/* Hero Section: Thumbnail + Title */}
        <section>
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            {hasImage && imageUrl ? (
              <div className="relative shrink-0 overflow-hidden rounded-lg">
                <ImageZoom src={imageUrl} caption={file.file_name}>
                  <img
                    src={file.thumbnail_url || imageUrl}
                    alt={file.file_name}
                    className="h-18 min-w-12 max-w-32 rounded-lg border border-border object-contain"
                  />
                </ImageZoom>
                <span className="pointer-events-none absolute bottom-1 right-1 flex size-6 items-center justify-center rounded-md border border-border bg-background/80 text-foreground backdrop-blur-sm">
                  <MagnifyingGlassPlusIcon className="size-3.5" />
                </span>
              </div>
            ) : (
              <div className="size-18 shrink-0 rounded-lg border border-border bg-muted flex items-center justify-center">
                {isImageFile(file.file_name) ? (
                  <PhotoIcon className="size-8 text-muted-foreground" />
                ) : (
                  <DocumentTextIcon className="size-8 text-muted-foreground" />
                )}
              </div>
            )}

            {/* Title and Context */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight wrap-break-word">
                    {fileName}
                  </h1>
                  {file.entity_name && (
                    <p className="text-sm font-medium text-muted-foreground line-clamp-2">
                      {file.entity_name}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      <DocumentArrowDownIcon className="size-4" />
                      Download
                    </a>
                  )}
                  <p className="inline-flex items-center rounded-md bg-mono-100 dark:bg-mono-800 px-2 py-1 text-xs font-medium text-mono-700 dark:text-mono-300 ring-1 ring-inset ring-mono-500/10 capitalize">
                    {file.entity_type}
                  </p>
                </div>
              </div>
              {metadata.length > 0 && (
                <div className="mt-0.5">
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <button className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                        <InformationCircleIcon className="size-4" />
                        File details
                      </button>
                    </Popover.Trigger>
                    <Popover.Content align="start" className="w-64 p-4 z-50">
                      <div className="space-y-3">
                        <h4 className="font-medium leading-none text-foreground">
                          File Details
                        </h4>
                        <div className="grid gap-3 text-sm">
                          {metadata.map(({ label, value }) => (
                            <div key={label} className="grid grid-cols-3 gap-2">
                              <span className="text-muted-foreground">
                                {label}
                              </span>
                              <span className="col-span-2 font-medium text-foreground truncate text-right">
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Popover.Content>
                  </Popover.Root>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Primary Content: Insights (Central Focus) */}
        {hasInsights && (
          <section className="space-y-4">
            {groupedInsights.length === 1 ? (
              <>
                <div className="flex items-center gap-2">
                  <SparklesIcon className="size-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">
                    AI Insights
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                    1 Analysis
                  </span>
                </div>
                <InsightCard group={groupedInsights[0]} />
              </>
            ) : (
              <TabsCapsule.Root defaultValue={groupedInsights[0].insight_type}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="size-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">
                      AI Insights
                    </h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                      {groupedInsights.length} Analyses
                    </span>
                  </div>
                  <TabsCapsule.List className="flex-wrap justify-evenly">
                    {groupedInsights.map((group) => {
                      const config =
                        insightConfig[group.insight_type as Insight_type] ??
                        defaultInsightConfig;
                      return (
                        <TabsCapsule.Trigger
                          key={group.insight_type}
                          value={group.insight_type}
                          className="gap-2"
                        >
                          <config.Icon
                            className={cn("size-4", config.iconColor)}
                          />
                          {config.name}
                        </TabsCapsule.Trigger>
                      );
                    })}
                  </TabsCapsule.List>
                </div>
                {groupedInsights.map((group) => (
                  <TabsCapsule.Content
                    key={group.insight_type}
                    value={group.insight_type}
                  >
                    <InsightCard group={group} />
                  </TabsCapsule.Content>
                ))}
              </TabsCapsule.Root>
            )}
          </section>
        )}

        {/* Loading state for insights */}
        {isInsightsLoading && (
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <SparklesIcon className="size-5 text-primary animate-pulse" />
              <h2 className="text-lg font-semibold text-foreground">
                Loading Insights...
              </h2>
            </div>
            <div className="rounded-2xl border border-border p-5 animate-pulse space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-full bg-mono-200 dark:bg-mono-800" />
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-mono-200 dark:bg-mono-800 rounded" />
                  <div className="h-3 w-16 bg-mono-200 dark:bg-mono-800 rounded" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full bg-mono-200 dark:bg-mono-800 rounded" />
                <div className="h-3 w-4/5 bg-mono-200 dark:bg-mono-800 rounded" />
              </div>
            </div>
          </section>
        )}

        {/* Incentives Section */}
        {!isIncentivesLoading && hasIncentives && (
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <TrophyIcon className="size-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-foreground">
                Incentives
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                {incentives!.reduce((sum, i) => sum + i.points, 0)} total pts
              </span>
            </div>

            <div className="grid gap-3">
              {incentives!.map((incentive) => (
                <IncentiveCard key={incentive.id} incentive={incentive} />
              ))}
            </div>
          </section>
        )}

        {/* Quick Actions Footer */}
        <footer className="flex flex-wrap items-center justify-center gap-3 pt-4 border-t border-border">
          {file.url && (
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-xl hover:bg-accent hover:border-accent-foreground/20 transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="size-4" />
              Open Original
            </a>
          )}
        </footer>
      </main>
    </div>
  );
};

export default ViewFile;
